import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useLayoutEffect,
  useContext
} from 'react';
// contexts

// modules
import { Stroke } from '../modules/sketchPad/stroke';
// types

import DrawingToolbar from './DrawingToolbar/DrawingToolBar';
import { SketchPad } from '../modules/sketchPad/sketchpad';
import { DrawingCanvasContext } from '../contexts/annotationContexts';
import {
  CustomizedBrushConfig,
  defaultBrushConfig
} from './DrawingToolbar/Brushes';
import { FullscreenButton } from '@cube3/ui/src/previews/VideoPreview/FullscreenButton';
import { AnotationCanvasFormat } from '@cube3/ui/src/Hooks/useVideoActualSize';
import { drawArrow } from './utils/drawArrow';
import { ReviewToolContext } from '../../../main/src/components/app/layout/ReviewTool/Context/reviewToolContext';

interface Props {
  saveAnnotation: (svg) => void;
  playedSeconds: number;
  format: AnotationCanvasFormat;
  annotationToggled: boolean;
  drawnSvg?: string;
  fullscreen?: boolean;
  translation?: {
    x: number;
    y: number;
  };
  toggleDrawingBar?: (open?: boolean) => void;
  toggleFullscreenAction?: () => void;
}

const DrawingCanvas = (props: Props) => {
  const {
    saveAnnotation,
    format,
    annotationToggled,
    drawnSvg,
    playedSeconds,
    fullscreen,
    translation,
    toggleFullscreenAction,
    toggleDrawingBar
  } = props;

  const [brushConfig, setBrushConfig] =
    useState<CustomizedBrushConfig>(defaultBrushConfig);
  // reset brush when exit edit mode;
  useEffect(() => {
    setBrushConfig(defaultBrushConfig);
  }, [annotationToggled, setBrushConfig]);

  // hooks
  const [drawing, setDrawing] = useState(false);

  const stroke = useRef<Stroke>(null);

  //// region: initialize skatchpad
  const [canvasElement, setCanvasElement] = useState<HTMLCanvasElement>(null);
  const [sketchPad, setSketchPad] = useState<SketchPad>(null);
  useEffect(() => {
    if (canvasElement) {
      setSketchPad(new SketchPad(canvasElement, { values: 'relative' }));
    }
  }, [canvasElement]);
  /// endof region

  // if jump to different play time, should reset canvas
  useEffect(() => {
    if (!sketchPad) return;
    sketchPad.clear();
    sketchPad.reset();
  }, [playedSeconds, sketchPad]);

  const onMouseDown = useCallback(
    (e) => {
      // prevent the image previewer from being moved by dragging and dropping.   
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      console.info('DEBUG drawingcanvas', 'onPointerDown');
      canvasElement?.setPointerCapture?.(e.pointerId);
      setDrawing(true);
      const brush = sketchPad?.newBrush(brushConfig);
      stroke.current = brush && sketchPad?.newStroke(brush);
    },
    [sketchPad, brushConfig]
  );

  const onMouseUp = useCallback(
    (e) => {
      if (drawing) {
        if (e.cancelable) e.preventDefault();
        const event = normalizeEvent(e, canvasElement);

        const brush = sketchPad?.newBrush(brushConfig);
        if (stroke?.current?.length === 0) {
          // Mouse didn't move, so it was a click. Add one point and draw it.
          const p = sketchPad.newPointFromEvent(event, true);

          sketchPad.addPointToStroke(
            stroke.current,
            [event.offsetX, event.offsetY],
            true
          );
          sketchPad.drawStroke(stroke.current, brush, false);
        } else if (brushConfig.drawArrow) {
          drawArrow(sketchPad, stroke, brush, format);
        } else {
          sketchPad.drawStroke(stroke.current, brush, false);
        }

        saveAnnotation(sketchPad.toSVG());
        setDrawing(false);
      }
    },
    [brushConfig, drawing, saveAnnotation, sketchPad, canvasElement, format]
  );

  const onMouseMove = useCallback(
    (e) => {
      if (drawing) {
        const event = normalizeEvent(e, canvasElement);
        const p = sketchPad.newPointFromEvent(event, true);
        const brush = sketchPad?.newBrush(brushConfig);
        sketchPad.addPointToStroke(stroke.current, p, true);
        sketchPad.drawStroke(stroke.current, brush, false);
      }
    },
    [brushConfig, drawing, sketchPad, stroke, canvasElement]
  );

  // add the eventListeners to the window, so we can track strokes that go outside the canvas
  useEffect(() => {
    window.addEventListener('pointerup', onMouseUp);
    window.addEventListener('pointercancel', onMouseUp);
    window.addEventListener('pointermove', onMouseMove, { passive: false });
    return () => {
      window.removeEventListener('pointerup', onMouseUp);
      window.removeEventListener('pointercancel', onMouseUp);
      window.removeEventListener('pointermove', onMouseMove);
    };
  }, [onMouseUp, onMouseMove]);


  // should display drawn/cached image
  const init = useRef(true);
  useEffect(() => {
    const brush = sketchPad?.newBrush();
    if (init.current && brush && sketchPad) {
      if (drawnSvg) {
        sketchPad.fromSVG(drawnSvg);
      }
      sketchPad.draw();
      init.current = false;
    }
  }, [sketchPad, drawnSvg, annotationToggled]);

  // handle resize
  useLayoutEffect(() => {
    if (!sketchPad || !format) return;
    // sketchPad.canvas.style.visibility = 'hidden';
    const f = setTimeout(() => {
      sketchPad.resize(format.width, format.height);
      // sketchPad.canvas.style.visibility = 'visible';
    }, 50);
  }, [format, sketchPad]);

  // adjust the tool bar position to ensure it remains bottom-center when pan-zoom image
  const rect = canvasElement?.getBoundingClientRect();

  const toolBarRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState(translation);

  const updateToolBarStyle = useCallback(() => {
    if (toolBarRef.current && transform) {
      const translateX = `calc(-50% - ${transform.x}px)`;

      toolBarRef.current.style.transition = 'transform 0.2s ease';
      toolBarRef.current.style.transform = `translate(${translateX}, ${-transform.y}px)`;
    }
  }, [translation, transform]);

  const updateTransform = useCallback(
    (event?: WheelEvent) => {
      if (
        !rect ||
        !translation ||
        (translation.x === 0 && translation.y === 0)
      ) {
        return;
      }
      const bottom = rect.bottom - window.innerHeight;

      if (
        rect.bottom > window.innerHeight &&
        (!transform || transform.y !== bottom)
      ) {
        setTransform({ x: translation.x, y: bottom });
      }
      if (transform?.y < 0) {
        setTransform({ x: translation.x, y: 0 });
      }
      // update the toolbar position when panning image
      if (event) {
        //console.log('move', { transform, translation, x: event.deltaX, rect });
        setTransform((prev) => ({
          x: Math.max(translation.x, prev.x - event.deltaX),
          y: Math.min(
            rect.height - toolBarRef.current?.clientHeight - 16,
            prev.y - event.deltaY
          )
        }));
      }
      requestAnimationFrame(updateToolBarStyle);
    },
    [rect, translation]
  );

  useEffect(() => {
    updateTransform();
    if (canvasElement && translation) {
      canvasElement.addEventListener('wheel', updateTransform, {
        passive: true
      });
      return () => canvasElement.removeEventListener('wheel', updateTransform);
    }
  }, [rect, translation]);


  const { setDrawnSvg } = useContext(ReviewToolContext)

  return (
    <>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <canvas
          id={'drawing_canvas'}
          ref={setCanvasElement}
          onPointerDown={onMouseDown}
          style={{
            width: `${format?.width}px`,
            height: format && format.height + 'px',
            touchAction: 'none'
          }}
          width={format && format.width}
          height={format && format.height}
        />
      </div>
      {/** hide DrawingToolbar when drawing*/}
      {drawing ? null : (
        <DrawingCanvasContext.Provider value={{ sketchPad }}>
          <DrawingToolbar
            ref={toolBarRef}
            brushConfig={brushConfig}
            setBrushConfig={setBrushConfig}
            onSave={saveAnnotation}
            closeDrawingBar={() => {
              toggleDrawingBar(false);
              setDrawnSvg('');
            }}
          />
        </DrawingCanvasContext.Provider>
      )}
      {/** in fullscreen view: add fullscreen toggle to the bottom right side */}
      <div style={{ position: 'absolute', bottom: 20, right: 24 }}>
        {fullscreen && toggleFullscreenAction && (
          <FullscreenButton
            fullscreen={true}
            toggleFullscreenAction={toggleFullscreenAction}
          />
        )}
      </div>
    </>
  );
};

export default DrawingCanvas;

// we want to track events outside the canvas, so we need
// to listen on the window, then map window coords to canvas local coords
const normalizeEvent = (
  e: PointerEvent | React.PointerEvent,
  canvas: HTMLCanvasElement
) => {
  const event: PointerEvent = (e as React.PointerEvent).nativeEvent || (e as any);
  const bb = canvas.getBoundingClientRect();

  return {
    timeStamp: event.timeStamp,
    offsetX: event.clientX - bb.x,
    offsetY: event.clientY - bb.y,
    target: canvas
  };
};
